import { api, formatError } from '../api-client'
import { analyticsEvent, flushAndExit } from '../analytics'
import { parseArgs } from './context/args'
import { emitJSON } from './context/render'
import { levenshtein } from './context/flags'
import { resolveWorkspaceSlug } from './context'

// Areas CLI — the navigation domains topics file under. Reads + writes are
// direct API calls; the dashboard renders the same list. An area is a curation
// surface (rename/delete re-home other people's topics' nav), so the API gates
// those to owner/admin — the CLI just relays the server's verdict.

export interface AreaSummary {
  id: string
  name: string
  description: string | null
  topic_count: number
}

const KNOWN_AREA_FLAGS: ReadonlySet<string> = new Set(['description', 'json', 'help'])

export function assertKnownAreaFlags(
  flags: Record<string, string | boolean>,
  onError: (msg: string) => never = (msg) => { console.error(msg); process.exit(1) },
): void {
  const unknown = Object.keys(flags).filter((f) => !KNOWN_AREA_FLAGS.has(f))
  if (unknown.length === 0) return
  const lines = unknown.map((f) => {
    let best: string | null = null
    let bestDist = Infinity
    for (const known of KNOWN_AREA_FLAGS) {
      const d = levenshtein(f, known)
      if (d < bestDist) { bestDist = d; best = known }
    }
    const hint = best !== null && bestDist <= Math.max(2, Math.floor(f.length / 2)) ? best : null
    return `  --${f}${hint ? `  (did you mean --${hint}?)` : ''}`
  })
  onError(
    `Error: unknown flag${unknown.length > 1 ? 's' : ''} for 'driftless area':\n` +
      lines.join('\n') +
      `\nRun 'driftless area --help' for valid flags.`,
  )
}

// Help lives in ./help-text (light, dep-free) so index.ts can print it without
// importing this command body; re-exported here so AREA_HELP still resolves from './area'.
export { AREA_HELP } from './help-text'
import { AREA_HELP } from './help-text'

function printHelp(stream: NodeJS.WritableStream = process.stdout): void {
  stream.write(AREA_HELP)
}

export async function areaCommand(args: string[]): Promise<void> {
  const { flags, positional } = parseArgs(args)
  const sub = positional[0]
  const isJSON = !!flags['json']

  if (flags['help'] === true || sub === 'help') { printHelp(); process.exit(0) }
  if (sub === undefined) { printHelp(process.stderr); process.exit(1) }
  assertKnownAreaFlags(flags)

  const ws = await resolveWorkspaceSlug()
  const base = `/workspaces/${ws}/areas`

  try {
    switch (sub) {
      case 'list': {
        const result = (await api.get(base)) as AreaSummary[]
        if (isJSON) { emitJSON(result); break }
        if (!result.length) { console.log('No areas yet. Create one with: driftless area add "<name>"'); break }
        for (const a of result) {
          const count = `${a.topic_count} topic${a.topic_count === 1 ? '' : 's'}`
          console.log(`${a.id}  ${a.name}  — ${count}${a.description ? `  (${a.description})` : ''}`)
        }
        break
      }
      case 'add': {
        const name = positional.slice(1).join(' ').trim()
        if (!name) { console.error('Error: an area name is required.'); process.exit(1) }
        const description = flags['description'] as string | undefined
        const result = (await api.post(base, { name, description })) as AreaSummary
        analyticsEvent('area_added', ws, {})
        if (isJSON) emitJSON(result)
        else console.log(`Area '${result.name}' created — id ${result.id}`)
        break
      }
      case 'rename': {
        const id = positional[1]
        const newName = positional.slice(2).join(' ').trim()
        if (!id) { console.error('Error: area id required. Usage: driftless area rename <id> <new-name>'); process.exit(1) }
        if (!newName) { console.error('Error: a new name is required. Usage: driftless area rename <id> <new-name>'); process.exit(1) }
        const result = (await api.patch(`${base}/${encodeURIComponent(id)}`, { name: newName })) as AreaSummary
        analyticsEvent('area_renamed', ws, {})
        if (isJSON) emitJSON(result)
        else console.log(`Area renamed → '${result.name}' (id ${result.id})`)
        break
      }
      case 'rm': {
        const id = positional[1]
        if (!id) { console.error('Error: area id required. Usage: driftless area rm <id>'); process.exit(1) }
        const result = (await api.delete(`${base}/${encodeURIComponent(id)}`)) as { deleted: string }
        analyticsEvent('area_removed', ws, {})
        if (isJSON) emitJSON(result)
        else console.log(`Area deleted — id ${result.deleted ?? id}`)
        break
      }
      default:
        console.error(`Unknown area subcommand: '${sub}'. Run 'driftless area --help'.`)
        process.exit(1)
    }
    await flushAndExit(0)
  } catch (e: any) {
    console.error(formatError(e))
    await flushAndExit(1)
  }
}

// Resolve a --area <name|id> value to an area id, auto-creating the area when a
// NAME doesn't yet exist. Shared by `context add` and `context update` so
// "migrate this knowledge to area X" / "create an area and file these here" is
// one flag. Returns the area id (or null when the flag wasn't passed).
export async function resolveAreaId(
  workspaceSlug: string,
  value: string | boolean | undefined,
  isJSON: boolean,
): Promise<string | null> {
  if (typeof value !== 'string' || !value.trim()) return null
  const needle = value.trim()
  const areas = (await api.get(`/workspaces/${workspaceSlug}/areas`)) as AreaSummary[]
  // Exact id match wins; then case-insensitive name match.
  const byId = areas.find((a) => a.id === needle)
  if (byId) return byId.id
  const byName = areas.find((a) => a.name.toLowerCase() === needle.toLowerCase())
  if (byName) return byName.id
  // No match → it's a new area name: auto-create it.
  const created = (await api.post(`/workspaces/${workspaceSlug}/areas`, { name: needle })) as AreaSummary
  if (!isJSON) console.log(`created area '${created.name}'`)
  return created.id
}
